import type { ComponentChildren } from "preact"
import { useEffect, useRef, useState } from "preact/hooks"

import { type Look } from "./appearance"

/**
 * The chat on a phone: a sheet up from the bottom edge instead of a card
 * floating beside a launcher there is no room for. It opens at half the
 * screen; a drag on the grab bar takes it to full height or, past the
 * bottom, away; a flick does the same faster. The visitor keeps the page
 * in view at half. The heights are percentages of a fixed element's
 * containing block - the viewport as it is, under a collapsing address
 * bar or a keyboard - not `vh`, which is the largest it ever gets.
 *
 * The snap is decided at release from where the sheet is and how fast it
 * moved: past three quarters is full, under a third is closed, a fast
 * flick wins over position. A pinned surface never closes - the drag
 * down settles at half again.
 */

/** Live, unlike `PageContext.device` (read once) - a rotation re-renders. */
export function useMobile(): boolean {
  const query = "(max-width: 639px)"
  const [mobile, setMobile] = useState(
    () => window.matchMedia?.(query).matches ?? false
  )
  useEffect(() => {
    const media = window.matchMedia?.(query)
    if (!media) return
    const sync = () => setMobile(media.matches)
    media.addEventListener("change", sync)
    return () => media.removeEventListener("change", sync)
  }, [])
  return mobile
}

type Snap = "half" | "full"

/** A flick this fast (px/ms) decides on its own, whatever the position. */
const FLICK = 0.5
const MIN_HEIGHT = 120

/** What `100%` on the sheet is: the viewport less any scrollbar. */
const viewportHeight = () => document.documentElement.clientHeight

export function Drawer({
  look,
  pinned,
  onClose,
  children,
}: {
  look: Look
  /** No close: a drag down settles at half, and there is no way off. */
  pinned: boolean
  onClose: () => void
  children: ComponentChildren
}) {
  const [snap, setSnap] = useState<Snap>("half")
  /** The height in px while a finger holds it; null between drags. */
  const [held, setHeld] = useState<number | null>(null)
  const sheet = useRef<HTMLDivElement>(null)
  const drag = useRef<{
    startY: number
    startHeight: number
    lastY: number
    lastAt: number
    velocity: number
  } | null>(null)

  const onPointerDown = (event: PointerEvent) => {
    const el = sheet.current
    if (!el) return
    ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
    drag.current = {
      startY: event.clientY,
      startHeight: el.getBoundingClientRect().height,
      lastY: event.clientY,
      lastAt: event.timeStamp,
      velocity: 0,
    }
    setHeld(drag.current.startHeight)
  }

  const onPointerMove = (event: PointerEvent) => {
    const d = drag.current
    if (!d) return
    const dt = event.timeStamp - d.lastAt
    if (dt > 0) d.velocity = (event.clientY - d.lastY) / dt
    d.lastY = event.clientY
    d.lastAt = event.timeStamp
    const next = d.startHeight + (d.startY - event.clientY)
    setHeld(Math.max(MIN_HEIGHT, Math.min(viewportHeight(), next)))
  }

  const onPointerUp = () => {
    const d = drag.current
    if (!d) return
    drag.current = null
    const height = Math.max(
      MIN_HEIGHT,
      Math.min(viewportHeight(), d.startHeight + (d.startY - d.lastY))
    )
    const vh = viewportHeight()
    // Flicks first: down from full is half, down from half is away, up is full.
    let next: Snap | "closed"
    if (d.velocity < -FLICK) next = "full"
    else if (d.velocity > FLICK) next = snap === "full" ? "half" : "closed"
    else if (height > vh * 0.75) next = "full"
    else if (height < vh * 0.3) next = "closed"
    else next = "half"
    setHeld(null)
    if (next === "closed") {
      if (pinned) setSnap("half")
      else onClose()
      return
    }
    setSnap(next)
  }

  const { ink, surfaceBg, rad } = look
  const full = snap === "full" && held === null
  const classes = [
    "uq-fixed uq-drawer uq-slide",
    full ? "uq-drawer-full" : "uq-drawer-half",
    held !== null && "uq-drawer-held",
  ]

  return (
    <div
      ref={sheet}
      class={classes.filter(Boolean).join(" ")}
      role="dialog"
      aria-modal="false"
      style={{
        height: held !== null ? held : undefined,
        borderRadius: full ? 0 : `${rad}px ${rad}px 0 0`,
        borderTop: `1px solid rgba(${ink},.12)`,
        background: surfaceBg,
        boxShadow: "0 -12px 40px rgba(0,0,0,.18)",
      }}
    >
      <div
        class="uq-grab"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        aria-label={full ? "Drag down to shrink or close" : "Drag up to expand"}
        style={{ background: surfaceBg }}
      >
        <span style={{ background: `rgba(${ink},.25)` }} />
      </div>
      {children}
    </div>
  )
}
